import type React from "react";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useReferral } from "@/hooks/useReferral";
import { useTreatments } from "@/hooks/useTreatments";
import { useWeightLogs } from "@/hooks/useWeightLogs";
import { useActionItems } from "@/hooks/useActionItems";
import { useCareTeam } from "@/hooks/useCareTeam";
import { useAssessments } from "@/hooks/useAssessments";
import { useVisits } from "@/hooks/useVisits";
import { useIntakeDrafts } from "@/hooks/useIntakeDrafts";
import { AppLayout } from "@/components/Layout";
import { IntakeResumeCard } from "@/components/IntakeResumeCard";
import { QuickMessageCard } from "@/components/QuickMessageCard";
import { Card } from "@/components/Card";
import { StatTile } from "@/components/StatTile";
import { StatusBadge } from "@/components/StatusBadge";
import { WeightChart } from "@/components/WeightChart";
import { LoadingScreen } from "@/components/LoadingScreen";
import { AppointmentCalendar } from "@/components/AppointmentCalendar";
import { BookingDialog } from "@/components/BookingDialog";
import { InfoModal } from "@/components/InfoModal";
import { QuickActions } from "@/components/QuickActions";
import { DragScrollRow } from "@/components/DragScrollRow";
import { Link } from "react-router-dom";
import { suggestedProducts, VERTICAL_LABEL } from "@/data/shopCatalog";
import {
  PillIcon, LabIcon, CalendarIcon, CardIcon, CheckinIcon, CheckIcon,
} from "@/components/icons";
import { useState, useMemo, useRef } from "react";
import type {
  PatientRow, TreatmentRow, WeightLogRow, ActionItemRow, CareTeamRow,
  AssessmentRow, ActionItemKind, TreatmentType,
} from "@/lib/database.types";

function initialsFor(p: PatientRow | null | undefined): string {
  if (!p) return "";
  return `${p.first_name?.[0] ?? ""}${p.last_name?.[0] ?? ""}`.toUpperCase();
}

function providerInitials(name: string | null | undefined): string {
  return (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function fmtHeight(inches: number | null): string {
  if (inches == null) return "—";
  const ft = Math.floor(inches / 12);
  const inch = inches % 12;
  return `${ft} ft ${inch} in`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function programName(t: TreatmentRow | null): string {
  if (!t) return "—";
  const med = t.medication.toLowerCase();
  if (med.includes("semaglutide")) return "Semaglutide";
  if (med.includes("tirzepatide")) return "Tirzepatide";
  if (med.includes("testosterone")) return "Testosterone";
  return t.medication;
}

export default function Dashboard() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const treatmentsQ = useTreatments(patientQ.data?.id);
  const weightQ = useWeightLogs(patientQ.data?.id);
  const actionsQ = useActionItems(patientQ.data?.id);
  const careTeamQ = useCareTeam(patientQ.data?.id);
  const assessmentsQ = useAssessments(patientQ.data?.id);
  const visitsQ = useVisits(patientQ.data?.id);
  const referralQ = useReferral(patientQ.data?.id);
  const draftsQ = useIntakeDrafts(session?.user.email ?? undefined);
  const [bookingOpen, setBookingOpen] = useState(false);

  if (patientQ.isLoading) return <LoadingScreen />;

  const patient = patientQ.data ?? null;
  const treatments = treatmentsQ.data ?? [];
  const activeTreatment = treatments.find((t) => t.status === "active") ?? treatments[0] ?? null;
  const weightLogs = weightQ.data ?? [];
  const currentWeight = weightLogs[0]?.weight_lb ?? null;
  const startingWeight = patient?.starting_weight_lb ?? null;
  const goalWeight = patient?.goal_weight_lb ?? null;
  const downLb =
    currentWeight != null && startingWeight != null
      ? Math.max(0, Number(startingWeight) - Number(currentWeight))
      : null;
  const toGoLb =
    currentWeight != null && goalWeight != null
      ? Math.max(0, Number(currentWeight) - Number(goalWeight))
      : null;
  const referralAction = referralQ.data?.next_action_url ?? null;

  return (
    <AppLayout initials={initialsFor(patient)}>
      {/* Header */}
      <header className="flex items-start justify-between mb-[22px] gap-4">
        <div className="min-w-0">
          <h1 className="font-serif text-[28px] font-normal text-fh-ink leading-tight mb-1">
            Welcome back{patient?.first_name ? `, ${patient.first_name}` : ""}
          </h1>
          <p className="text-[14px] text-fh-gray m-0">
            Here's where your care stands today.
          </p>
        </div>
        {patient && (
          <div className="lg:hidden w-[42px] h-[42px] rounded-full bg-fh-green-dark text-white flex items-center justify-center text-[15px] font-semibold shrink-0">
            {initialsFor(patient)}
          </div>
        )}
      </header>

      <QuickActions />

      {/* Cross-sell strip — sits right under quick actions for visibility. */}
      <div className="mb-4">
        <SuggestedForYouCard
          treatments={treatments}
          assessments={assessmentsQ.data ?? []}
        />
      </div>

      {/* Stat tiles */}
      <div className="flex gap-3.5 mb-4 flex-wrap">
        <StatTile
          label="Current weight"
          value={currentWeight != null ? `${Number(currentWeight).toFixed(0)} lb` : "—"}
          hint={
            downLb != null && downLb > 0
              ? `down ${downLb.toFixed(0)} lb`
              : currentWeight != null
                ? "starting point"
                : undefined
          }
        />
        <StatTile
          label="Goal weight"
          value={goalWeight != null ? `${Number(goalWeight).toFixed(0)} lb` : "—"}
          hint={
            toGoLb != null && toGoLb > 0
              ? `${toGoLb.toFixed(0)} lb to go`
              : goalWeight != null && currentWeight != null
                ? "goal reached"
                : undefined
          }
        />
        <StatTile
          label="Next refill"
          value={fmtDate(activeTreatment?.next_refill_at ?? null)}
          hint={(() => {
            const d = daysUntil(activeTreatment?.next_shipment_at ?? null);
            if (d == null) return undefined;
            if (d < 0) return `shipped ${Math.abs(d)} days ago`;
            if (d === 0) return "ships today";
            return `ships in ${d} day${d === 1 ? "" : "s"}`;
          })()}
        />
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Resume-your-assessment card — only renders when the patient has
            an in-flight intake draft (e.g. started WL while in the portal
            for TRT, or abandoned mid-flow). Sits above everything so it's
            the first thing they see. */}
        {(draftsQ.data ?? []).length > 0 && (
          <div className="lg:col-span-2">
            <IntakeResumeCard drafts={draftsQ.data ?? []} />
          </div>
        )}
        {/* Quick message widget sits above appointments — primary support
            channel for patients. Full-width so the textarea has room and
            the recent-reply preview reads cleanly. */}
        <div className="lg:col-span-2">
          <QuickMessageCard patientId={patient?.id} />
        </div>
        <div className="lg:col-span-2">
          <Card title="Appointments" action={
            <Link to="/visits" className="text-[12px] font-semibold text-fh-green hover:text-fh-green-dark">
              See all →
            </Link>
          }>
            <AppointmentCalendar
              visits={visitsQ.data ?? []}
              onBook={() => setBookingOpen(true)}
            />
          </Card>
        </div>
        <TreatmentCard treatment={activeTreatment} />
        <SubscriptionsCard treatments={treatments} />
        <ActionItemsCard items={actionsQ.data ?? []} fallbackUrl={referralAction} />
        <CareTeamCard team={careTeamQ.data ?? []} />
        <IntakeSummaryCard patient={patient} assessments={assessmentsQ.data ?? []} />
        <ProgressCard logs={weightLogs} />
      </div>

      <p className="text-center text-[11.5px] text-fh-gray mt-6">
        Your health information is private, encrypted, and never shared without your consent.
      </p>

      <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// 4. Your treatment ---------------------------------------------------------
function TreatmentCard({ treatment }: { treatment: TreatmentRow | null }) {
  if (!treatment) {
    return (
      <Card title="Your treatment">
        <p className="text-[14px] text-fh-gray leading-relaxed">
          Your treatment plan will appear here once your provider has approved your regimen.
        </p>
      </Card>
    );
  }

  const program = programName(treatment);
  const plan = treatment.plan_duration_months
    ? `${treatment.plan_duration_months}-month plan`
    : null;
  const startedMonth =
    treatment.started_at
      ? `Month ${Math.max(
          1,
          Math.floor(
            (Date.now() - new Date(treatment.started_at).getTime()) /
              (1000 * 60 * 60 * 24 * 30),
          ) + 1,
        )}`
      : null;
  const subtitle = [treatment.dose, startedMonth, plan].filter(Boolean).join(" · ");
  const statusTone: "active" | "pending" | "muted" | "warn" = treatment.status === "active"
    ? "active"
    : treatment.status === "pending_review"
      ? "pending"
      : treatment.status === "paused"
        ? "muted"
        : "warn";
  const statusLabel = treatment.status === "active"
    ? "Active"
    : treatment.status === "pending_review"
      ? "Pending review"
      : treatment.status === "paused"
        ? "Paused"
        : "Discontinued";

  return (
    <Card
      title="Your treatment"
      action={
        <Link to="/treatment" className="text-fh-green hover:text-fh-green-dark">
          View details →
        </Link>
      }
    >
      <div className="flex items-center gap-3 mb-3.5">
        <div className="w-[46px] h-[46px] rounded-[12px] bg-fh-sage flex items-center justify-center shrink-0">
          <PillIcon filledFraction={0.5} />
        </div>
        <div>
          <div className="text-[15px] font-semibold text-fh-ink">{program}</div>
          {subtitle && <div className="text-[13px] text-fh-gray">{subtitle}</div>}
        </div>
      </div>
      <StatusBadge label={statusLabel} tone={statusTone} />
      {treatment.next_shipment_at && (
        <div className="text-[13px] text-fh-gray mt-3 leading-snug">
          Next shipment ships {fmtDate(treatment.next_shipment_at)}
          {treatment.next_refill_at && (
            <>, refill due {fmtDate(treatment.next_refill_at)}</>
          )}.
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 5. Action items ----------------------------------------------------------
const kindMeta: Record<ActionItemKind, { icon: React.ComponentType; label: string }> = {
  lab:              { icon: LabIcon,      label: "Lab" },
  visit:            { icon: CalendarIcon, label: "Visit" },
  payment:          { icon: CardIcon,     label: "Payment" },
  check_in:         { icon: CheckinIcon,  label: "Check-in" },
  intake_followup:  { icon: CheckIcon,    label: "Intake" },
  other:            { icon: CheckIcon,    label: "Action" },
};

function ActionItemsCard({
  items,
  fallbackUrl,
}: {
  items: ActionItemRow[];
  fallbackUrl: string | null;
}) {
  if (items.length === 0) {
    return (
      <Card title="Action items">
        <p className="text-[14px] text-fh-gray-light leading-relaxed">
          You're all caught up. We'll let you know when anything needs your attention.
        </p>
      </Card>
    );
  }

  const primary = items[0]!;
  const Icon = kindMeta[primary.kind].icon;

  return (
    <Card title="Action needed" tone="accent">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-[10px] bg-white text-fh-green-dark flex items-center justify-center shrink-0">
          <Icon />
        </div>
        <div className="min-w-0">
          <div className="text-[14.5px] font-semibold text-fh-ink">{primary.title}</div>
          {primary.body && (
            <div className="text-[13px] text-fh-green-dark mt-1 leading-snug">{primary.body}</div>
          )}
          {primary.due_at && (
            <div className="text-[12px] text-fh-gray mt-1.5">
              Due {fmtDate(primary.due_at)}
            </div>
          )}
        </div>
      </div>
      <a
        href={primary.action_url ?? fallbackUrl ?? "#"}
        target={primary.action_url || fallbackUrl ? "_blank" : undefined}
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-[9px] bg-fh-green text-white text-[13px] font-semibold px-4 py-2 hover:bg-fh-green-dark"
      >
        {primary.kind === "check_in"
          ? "Complete check-in"
          : primary.kind === "lab"
            ? "Order lab"
            : primary.kind === "visit"
              ? "Schedule visit"
              : primary.kind === "payment"
                ? "Update payment"
                : "Take action"}
      </a>

      {items.length > 1 && (
        <ul className="mt-4 pt-3 border-t border-fh-green-light/50 divide-y divide-fh-green-light/50">
          {items.slice(1).map((it) => {
            const I = kindMeta[it.kind].icon;
            return (
              <li key={it.id} className="flex items-center gap-3 py-2.5 text-[13px] text-fh-green-dark">
                <I />
                <span className="flex-1 min-w-0 truncate">{it.title}</span>
                {it.due_at && (
                  <span className="text-[12px] text-fh-gray shrink-0">{fmtDate(it.due_at)}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 6. Intake summary -------------------------------------------------------
// Renders pathway-specific rows because WL and TRT intakes capture different
// fields. The shapes are typed in database.types.ts (WLIntakeResponses /
// TRTIntakeResponses); both inherit common keys (height, weight, conditions,
// allergies, etc.). If the patient has assessments for both pathways, a tab
// strip at the top lets them toggle between summaries.
function IntakeSummaryCard({
  patient,
  assessments,
}: {
  patient: PatientRow | null;
  assessments: AssessmentRow[];
}) {
  // Pick the latest assessment per pathway (assessments arrive newest-first).
  const byType = useMemo(() => {
    const map: Partial<Record<TreatmentType, AssessmentRow>> = {};
    for (const a of assessments) {
      if (!map[a.treatment_type]) map[a.treatment_type] = a;
    }
    return map;
  }, [assessments]);

  const availableTypes = useMemo(
    () => (["wl", "trt"] as TreatmentType[]).filter((t) => !!byType[t]),
    [byType],
  );

  // Default to the most-recently-submitted assessment's pathway.
  const defaultType: TreatmentType | null = assessments[0]?.treatment_type ?? null;
  const [selected, setSelected] = useState<TreatmentType | null>(defaultType);

  // Keep selection valid if the assessment set changes.
  const effective: TreatmentType | null =
    selected && availableTypes.includes(selected)
      ? selected
      : (availableTypes[0] ?? null);

  if (!patient && assessments.length === 0) {
    return (
      <Card title="Intake summary" action="From your assessment">
        <p className="text-[14px] text-fh-gray-light">
          Your intake details will show up here once you complete an assessment.
        </p>
      </Card>
    );
  }

  const assessment: AssessmentRow | null = effective ? byType[effective] ?? null : null;
  const tt = assessment?.treatment_type;
  const responses = (assessment?.responses ?? {}) as Record<string, unknown>;
  const pathwayLabel = tt === "trt" ? "Testosterone" : tt === "wl" ? "Weight Loss" : "Assessment";

  // Helpers for safe extraction from the JSONB blob.
  const str = (v: unknown): string | null =>
    typeof v === "string" && v.trim() !== "" ? v : null;
  const arr = (v: unknown): string | null =>
    Array.isArray(v) && v.length > 0
      ? v.map((x) => String(x)).join(", ")
      : null;
  const num = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) return Number(v);
    return null;
  };

  // Shared vitals — Height / Starting weight / Starting BMI, always shown.
  const common: [string, string][] = [
    ["Height", fmtHeight(patient?.height_inches ?? null)],
    [
      "Starting weight",
      patient?.starting_weight_lb != null
        ? `${Number(patient.starting_weight_lb).toFixed(0)} lb`
        : "—",
    ],
    ["Starting BMI", assessment?.bmi != null ? Number(assessment.bmi).toFixed(1) : "—"],
  ];

  // Pathway-specific rows.
  let pathway: [string, string][] = [];

  if (tt === "wl") {
    const goalW = num(responses.goalweight);
    pathway = [
      ["Goal weight", goalW != null ? `${goalW.toFixed(0)} lb` : "—"],
      ["What's driving the goal", arr(responses.motivation) ?? "—"],
      ["GLP-1 history", str(responses.glp1) ?? "—"],
      ["Tried before", arr(responses.tried) ?? "—"],
      ["Blood pressure", str(responses.bloodPressure) ?? "—"],
      ["Resting heart rate", str(responses.heartRate) ?? "—"],
      ["Medical conditions", arr(responses.conditions) ?? "None reported"],
      ["Prior weight-loss surgery", str(responses.prevSurgery) ?? "—"],
      ["Opiate use (last 3 months)", str(responses.opiateUse) ?? "—"],
      ["Currently takes prescription meds", str(responses.rxGate) ?? "—"],
      ["Current medications", str(responses.medsList) ?? "None reported"],
      ["Allergies", str(responses.allergies) ?? "None reported"],
    ];
    if (str(responses.pregnancy)) {
      pathway.push(["Pregnancy", str(responses.pregnancy)!]);
    }
    if (str(responses.additionalInfo)) {
      pathway.push(["Anything else", str(responses.additionalInfo)!]);
    }
  } else if (tt === "trt") {
    pathway = [
      ["Top symptoms", arr(responses.symptoms) ?? "—"],
      ["Symptom duration", str(responses.symptomDuration) ?? "—"],
      ["Prior testosterone testing", str(responses.priorT) ?? "—"],
      ["Prior testosterone therapy", str(responses.priorTRT) ?? "—"],
      ["Cardiovascular history", str(responses.cardio) ?? "None reported"],
      ["Medical conditions", arr(responses.conditions) ?? "None reported"],
      ["High-interaction meds", arr(responses.rxFlags) ?? "None reported"],
      ["Sleep duration", str(responses.sleep) ?? "—"],
      ["Sleep quality", str(responses.sleepQuality) ?? "—"],
      ["Alcohol intake", str(responses.alcohol) ?? "—"],
      ["Exercise", str(responses.exercise) ?? "—"],
      ["Family planning", str(responses.fertility) ?? "—"],
      ["Goals", arr(responses.goals) ?? "—"],
      ["Current medications", str(responses.medsList) ?? "None reported"],
      ["Allergies", str(responses.allergies) ?? "None reported"],
    ];
  }

  const showTabs = availableTypes.length > 1;

  return (
    <Card
      title="Intake summary"
      action={
        !showTabs ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-fh-sand text-fh-green-dark">
            {pathwayLabel}
          </span>
        ) : undefined
      }
    >
      {showTabs && (
        <div className="flex gap-1 -mt-1 mb-3 border-b border-fh-border">
          {availableTypes.map((t) => {
            const active = t === effective;
            const label = t === "wl" ? "Weight Loss" : "Testosterone";
            return (
              <button
                key={t}
                type="button"
                onClick={() => setSelected(t)}
                className={[
                  "px-3 py-2 text-[13px] font-semibold border-b-2 -mb-px transition",
                  active
                    ? "text-fh-green-dark border-fh-green"
                    : "text-fh-gray border-transparent hover:text-fh-ink",
                ].join(" ")}
                aria-pressed={active}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {assessment ? (
        <>
          <div className="divide-y divide-fh-border">
            {[...common, ...pathway].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 py-[9px] text-[13.5px]">
                <span className="text-fh-gray shrink-0">{label}</span>
                <span className="font-semibold text-fh-ink text-right break-words">{value}</span>
              </div>
            ))}
          </div>
          {assessment.submitted_at && (
            <p className="text-[12px] text-fh-gray italic mt-3">
              Captured {new Date(assessment.submitted_at).toLocaleDateString()}.
            </p>
          )}
        </>
      ) : (
        <p className="text-[14px] text-fh-gray-light">
          Your intake details will show up here once you complete an assessment.
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 7. Care team ------------------------------------------------------------
function CareTeamCard({ team }: { team: CareTeamRow[] }) {
  const [open, setOpen] = useState(false);

  if (team.length === 0) {
    return (
      <Card title="Your care team">
        <p className="text-[14px] text-fh-gray-light leading-relaxed">
          Once you're matched with a clinician, they'll appear here.
        </p>
      </Card>
    );
  }

  const primary = team[0]!;
  const initials = providerInitials(primary.provider?.full_name);

  return (
    <>
      <Card
        title="Your care team"
        action={
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-fh-green hover:text-fh-green-dark"
          >
            View details →
          </button>
        }
      >
        <div className="flex items-center gap-3 mb-3.5">
          <div className="w-[46px] h-[46px] rounded-full bg-fh-sand flex items-center justify-center text-[15px] font-semibold text-fh-green-dark shrink-0">
            {initials}
          </div>
          <div>
            <div className="text-[14px] font-semibold text-fh-ink">{primary.provider?.full_name ?? "Provider"}</div>
            <div className="text-[12.5px] text-fh-gray capitalize">{primary.role.replace("_", " ")}</div>
          </div>
        </div>
        {primary.bio && (
          <div className="bg-fh-cream rounded-[10px] px-3.5 py-3 text-[13px] text-fh-ink leading-snug">
            "{primary.bio}"
          </div>
        )}
        {team.length > 1 && (
          <ul className="mt-3 pt-3 border-t border-fh-border divide-y divide-fh-border">
            {team.slice(1).map((m) => (
              <li key={m.id} className="flex justify-between py-2 text-[13px]">
                <span className="font-semibold text-fh-ink">{m.provider?.full_name ?? "Provider"}</span>
                <span className="text-fh-gray capitalize">{m.role.replace("_", " ")}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <InfoModal
        open={open}
        onClose={() => setOpen(false)}
        title="Your care team"
        subtitle="The licensed clinicians and coordinators supporting your care."
      >
        <ul className="divide-y divide-fh-border">
          {team.map((m) => (
            <li key={m.id} className="flex items-start gap-3 py-3.5">
              <div className="w-11 h-11 rounded-full bg-fh-sand flex items-center justify-center text-[14px] font-semibold text-fh-green-dark shrink-0">
                {providerInitials(m.provider?.full_name)}
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-fh-ink">{m.provider?.full_name ?? "Provider"}</div>
                <div className="text-[12.5px] text-fh-gray capitalize mb-1">{m.role.replace("_", " ")}</div>
                {m.bio && <p className="text-[13px] text-fh-ink leading-snug">{m.bio}</p>}
              </div>
            </li>
          ))}
        </ul>
      </InfoModal>
    </>
  );
}

// ---------------------------------------------------------------------------
// 8. Your progress --------------------------------------------------------
function ProgressCard({ logs }: { logs: WeightLogRow[] }) {
  return (
    <Card
      title="Your progress"
      action={
        <Link to="/progress" className="text-fh-green hover:text-fh-green-dark">
          View details →
        </Link>
      }
    >
      <WeightChart logs={logs} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Suggested for you — compact cross-sell strip fed by the same rule engine
// as the Shop page. Rx suggestions route to assessments, never add-to-cart.
function SuggestedForYouCard({
  treatments,
  assessments,
}: {
  treatments: TreatmentRow[];
  assessments: AssessmentRow[];
}) {
  // Wider pool than fits on screen — the row scrolls horizontally to browse.
  const picks = suggestedProducts(treatments, assessments, 8);
  const rowRef = useRef<HTMLDivElement>(null);
  if (picks.length === 0) return null;
  const scrollRow = (dir: 1 | -1) =>
    rowRef.current?.scrollBy({ left: dir * 484, behavior: "smooth" });
  const arrowCls =
    "w-7 h-7 grid place-items-center rounded-full border border-fh-border bg-white text-fh-gray hover:text-fh-ink hover:border-fh-green-light transition";
  return (
    <Card
      title="Suggested for you"
      action={
        <span className="flex items-center gap-2">
          {/* Explicit arrows — the row's scrollbar is hidden, so mouse users
              need a visible way to page through the suggestions. */}
          <button type="button" aria-label="Scroll suggestions left" className={arrowCls}
            onClick={() => scrollRow(-1)}>
            ‹
          </button>
          <button type="button" aria-label="Scroll suggestions right" className={arrowCls}
            onClick={() => scrollRow(1)}>
            ›
          </button>
          <Link to="/shop" className="text-fh-green hover:text-fh-green-dark ml-1">
            Browse the shop →
          </Link>
        </span>
      }
    >
      <DragScrollRow ref={rowRef} ariaLabel="Suggested products" className="gap-3 pb-1">
        {picks.map((p) => (
          <Link
            key={p.id}
            to="/shop"
            draggable={false}
            className="shrink-0 w-[230px] rounded-[12px] border border-fh-border bg-fh-cream p-3.5 hover:border-fh-green-light transition-colors"
          >
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider bg-fh-sand text-fh-green-dark">
              {VERTICAL_LABEL[p.vertical]}
            </span>
            <div className="text-[14px] font-semibold text-fh-ink mt-1.5">{p.name}</div>
            <div className="text-[12px] text-fh-gray">{p.tagline}</div>
            <div className="text-[12px] font-semibold text-fh-green-dark mt-1.5">{p.priceLabel}</div>
          </Link>
        ))}
      </DragScrollRow>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Subscriptions — plan & billing view of the patient's treatments, distinct
// from the clinical "Your treatment" card. Manage routes to /billing.
function SubscriptionsCard({ treatments }: { treatments: TreatmentRow[] }) {
  const subs = treatments.filter((t) => t.status === "active" || t.status === "paused");
  return (
    <Card
      title="Subscriptions"
      action={
        <Link to="/billing" className="text-fh-green hover:text-fh-green-dark">
          Manage →
        </Link>
      }
    >
      {subs.length === 0 ? (
        <p className="text-[14px] text-fh-gray leading-relaxed">
          Your plan and billing details will appear here once your subscription is active.
        </p>
      ) : (
        <ul className="divide-y divide-fh-border -my-1">
          {subs.map((t) => {
            const cadence = t.plan_duration_months
              ? `${t.plan_duration_months}-month plan`
              : "Monthly plan";
            const meta = [
              t.next_refill_at ? `Next payment ${fmtDate(t.next_refill_at)}` : null,
              t.next_shipment_at ? `ships ${fmtDate(t.next_shipment_at)}` : null,
            ].filter(Boolean).join(" · ");
            return (
              <li key={t.id} className="flex items-center gap-3 py-3">
                <div className="w-9 h-9 rounded-[10px] bg-fh-sage flex items-center justify-center shrink-0 text-fh-green-dark">
                  <CardIcon />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-fh-ink truncate">
                    {programName(t)} · {cadence}
                  </div>
                  <div className="text-[12.5px] text-fh-gray">{meta || "Billing active"}</div>
                </div>
                <StatusBadge
                  label={t.status === "active" ? "Active" : "Paused"}
                  tone={t.status === "active" ? "active" : "muted"}
                />
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
